import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { GenericForm } from "@/Patient/Models/Forms/GenericForm";
import { RadPdfForm } from "@/Patient/Models/Forms/RadPdf/RadPdfForm";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";

import img_icon_addfavorite from "@/Globals/_images/icons/AddFavorite.png";
import img_icon_favorite from "@/Globals/_images/icons/Favorite.png";

interface IRadPdfFormsSelectionComponentProps {
    encounter: Encounter;
    onDuplicate: (form: RadPdfForm) => void;
    onContinue?: (form: RadPdfForm, isFormAddedToEncounter: boolean) => void;
    onClose: () => void;
}

class RadPdfFormsSelectionComponentState {
    public newFormDisplayableList: GenericForm[];
    public allRecentlyUsedForms: RadPdfForm[];
    public recentlyUsedForms: RadPdfForm[];
    public searchText: string;
    public shouldShowViewMore: boolean;
    public allForms: SearchableItemModelT<GenericForm>[];
    public newFormLimit: number;

    public static GetDefault(): RadPdfFormsSelectionComponentState {
        return {
            newFormDisplayableList: [],
            allRecentlyUsedForms: [],
            recentlyUsedForms: [],
            allForms: [],
            shouldShowViewMore: true,
            newFormLimit: 50,
            searchText: ""
        } as RadPdfFormsSelectionComponentState;
    }
}

class RadPdfFormsSelectionComponent extends BaseReactComponent<IRadPdfFormsSelectionComponentProps, RadPdfFormsSelectionComponentState> {
    private readonly componentId = "RadPdfFormsSelectionComponent";
    private currentEmployee: Employee;

    constructor(props: IRadPdfFormsSelectionComponentProps) {
        super(props);

        this.state = RadPdfFormsSelectionComponentState.GetDefault();
    }

    public componentDidMount(): void {
        this.setOverlay(this.componentId);
        this.currentEmployee = cacheStore.GetCurrentEmployee();

        this.loadNewForms();
        this.loadRecentlyUsedForms()
            .then(response => {
                if (!response)
                    return;

                this.mutateState(s => {
                    s.allRecentlyUsedForms = (response.Forms || []).map(x => new RadPdfForm(x));
                    s.recentlyUsedForms = (response.Forms || []).map(x => new RadPdfForm(x));
                });
            });
    }

    public getDerivedStateFromProps(nextProps: IRadPdfFormsSelectionComponentProps, prevState: IRadPdfFormsSelectionComponentProps): RadPdfFormsSelectionComponentState {
        if (nextProps.encounter?.Id !== prevState.encounter?.Id)
            return RadPdfFormsSelectionComponentState.GetDefault();

        return null;
    }

    public render(): JSX.Element {
        if (!this.state)
            return null;

        return (
            <Popup
                Id={this.componentId}
                title={cacheStore.GetString("AddFormLabel")}
                centeredHorizontally
                draggable={false}
                topPosition={35}
                onCloseDelegate={() => this.close()}
            >
                <div className="p-body" style={{ width: "900px", maxHeight: "650px", overflow: "hidden", padding: "0 25px" }}>
                    {this.renderSearchSection()}
                    <div style={{ maxHeight: "580px", overflowY: "auto" }}>
                        <div>
                            {this.renderRecentlyUsedFormsSection()}
                            {this.renderNewFormsSection()}
                        </div>
                    </div>
                </div>

                <div className="p-footer text-right">
                    <button className="btn btn-default" onClick={() => this.close()}>{cacheStore.GetString("CancelLabel")}</button>
                </div>
            </Popup>
        );
    }

    private renderSearchSection(): JSX.Element {
        return (
            <div style={{ padding: "15px 0" }}>
                <div className="search-field" style={{ width: "100%", maxHeight: "60px" }}>
                    <div className="gf">
                        <span className="glyphicon glyphicon-search" />
                    </div>
                    <TextboxControl
                        onMountFocus
                        debounceMs={400}
                        value={this.state.searchText}
                        name="text_search_List"
                        className="fullWidth"
                        operation={null}
                        placeholder={cacheStore.GetString("SearchLabel")}
                        autoComplete="off"
                        onChange={value => this.handleOnSearchChange(value)}
                        noAutoCompleteStyle={{ width: "100%" }}
                    />
                </div>
            </div>
        );
    }

    private renderFormListSections(label: string, formList: JSX.Element[]): JSX.Element {
        if (!formList?.length)
            return null;

        return (
            <div style={{ padding: "15px 0" }}>
                <h4 style={{ fontWeight: "bold" }}>{cacheStore.GetString(label)}</h4>
                <div style={{ padding: "0 15px" }}>
                    <ul>
                        {formList}
                    </ul>
                </div>
            </div>
        );
    }

    private renderRecentlyUsedFormsSection(): JSX.Element {
        const maxList = 5;

        let recentlyUsedFormList = this.state.recentlyUsedForms.map((form, idx) => {
            let style: React.CSSProperties = { padding: "10px 5px" };
            if (idx !== 0)
                style.borderTop = "1px solid #ccc";

            return (
                <li key={form.PatientFormId} style={style}>
                    <div className="flex-container">
                        <div>
                            <strong>{form.LastUpdateDate && moment(form.LastUpdateDate).format("YYYY-MM-DD")}</strong>
                            <span style={{ padding: "0 5px" }}>-</span>
                            <span title={form.FormName}>{MyleHelper.ShortenString(form.FormName, 80)}</span>
                        </div>
                        <div>
                            {this.state.allForms.any(x => x.Model.formName === form.FormName) && (
                                <span>
                                    <a onClick={() => this.handleDuplicatePatientFormClick(form)}>{cacheStore.GetString("DuplicateLabel")}</a>
                                    <span style={{ padding: "0 5px" }}>|</span>
                                </span>
                            )}
                            <a onClick={() => this.handleOnContinueClick(form)}>{cacheStore.GetString("ContinueLabel")}</a>
                        </div>
                    </div>
                </li>
            );
        });

        if (recentlyUsedFormList.length > maxList) {
            if (this.state.shouldShowViewMore)
                recentlyUsedFormList = recentlyUsedFormList.slice(0, maxList);

            recentlyUsedFormList.push(this.renderViewMore());
        }

        return this.renderFormListSections("RecentlyUsedInThisFileLabel", recentlyUsedFormList);
    }

    private renderViewMore(): JSX.Element {
        return (
            <li key="view-more" style={{ padding: "10px 5px" }}>
                <a onClick={() => this.mutateState(s => s.shouldShowViewMore = !s.shouldShowViewMore)}>
                    {cacheStore.GetString(this.state.shouldShowViewMore ? "ViewMoreLabel" : "ViewLessLabel")}
                </a>
            </li>
        );
    }

    private renderNewFormsSection(): JSX.Element {
        let newFormList = this.state.newFormDisplayableList.slice(0, this.state.newFormLimit).map((form, idx) => {
            let style: React.CSSProperties = { padding: "10px 5px" };
            if (idx !== this.state.newFormDisplayableList.length - 1)
                style.borderBottom = "1px solid #ccc";

            return (
                <div key={form.formId} style={style}>
                    <div style={{ display: "inline", verticalAlign: "text-bottom" }}>{this.renderFavoriteIcon(form)}</div>
                    <span style={{ padding: "0 5px" }} />
                    <a style={{ textDecoration: "none" }} onClick={() => this.handleOnNewFormItemClick(form)}>
                        {form.formName}
                    </a>
                </div>
            );
        });

        return (
            <>
                {this.renderFormListSections("NewFormLabel", newFormList)}
                <li key="view-more" style={{ padding: "10px 5px" }}>
                    {this.state.newFormLimit < this.state.newFormDisplayableList.length && (
                        <a style={{ marginRight: "5px" }} onClick={() => this.mutateState(s => s.newFormLimit += 50)}>
                            {cacheStore.GetString("ViewMoreLabel")}
                        </a>
                    )}
                </li>
            </>
        );
    }

    private renderFavoriteIcon(form: GenericForm): JSX.Element {
        return (
            <span style={{ cursor: "pointer" }} onClick={() => this.handleToggleFavoriteClick(form)}>
                <img
                    style={{ height: "14px", marginRight: "5px" }}
                    id={form.formId + "_favorite"}
                    src={form.isFavorite ? img_icon_favorite : img_icon_addfavorite}
                />
            </span>
        );
    }

    private showForceDuplicatePopup(form: RadPdfForm): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("RadPdfVersionErrorLabel"),

            showCancel: true,
            cancelAction: () => this.close(),

            showOk: true,
            okLabel: cacheStore.GetString("CreateNewLabel"),
            okAction: () => this.handleOnNewFormItemClick(form.toGenericForm()),

            altButtonLabel: cacheStore.GetString("ContinueLabel"),
            altButtonVariant: "brand",
            altButtonAction: () => this.handleDuplicatePatientFormClick(form, true)
        });
        this.close();
    }

    private handleOnSearchChange(value: string): void {
        if (value !== this.state.searchText)
            this.mutateState(s => {
                s.searchText = value;
                s.newFormDisplayableList = s.allForms.filterSearch(value, x => x.Value_Searchable).map(x => x.Model).sortByPropertyDesc(x => x.isFavorite);
                s.recentlyUsedForms = s.allRecentlyUsedForms.filterSearch(value, x => x.FormName).map(x => new RadPdfForm(x));
            });
    }

    private async handleToggleFavoriteClick(form: GenericForm): Promise<void> {
        const response = form.isFavorite
            ? await this.removeFavorite(form)
            : await this.addFavorite(form);

        if (!response)
            return;

        this.mutateState(s => s.newFormDisplayableList = s.newFormDisplayableList.mutate(f => f.isFavorite = !form.isFavorite, f => f.formId === form.formId));
        cacheStore.updateFavoriteFormCache(form);
    }

    private handleOnNewFormItemClick(form: GenericForm): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.CreateRadPdfForm, { formId: form.formId, form: form.formName });
        this.close();
    }

    private async handleDuplicatePatientFormClick(form: RadPdfForm, forceDuplicate: boolean = false): Promise<void> {
        const duplicatedForm = await this.duplicatePatientForm(form, forceDuplicate);

        if (!duplicatedForm) {
            this.close();
            return null;
        }

        if (duplicatedForm.IsPdfVersionConflictDetected)
            this.showForceDuplicatePopup(form);
        else
            this.openRadPdfFormWithCallback(
                duplicatedForm,
                null,
                () => this.props.onDuplicate && this.props.onDuplicate(duplicatedForm)
            );
    }

    private openRadPdfFormWithCallback(form: RadPdfForm, lastForm: RadPdfForm, callback: () => void): void {
        try {
            Client.openRadpdfForm(
                form.PatientFormId,
                this.currentEmployee.ContactFacet_id,
                form.EncounterId,
                form.EncounterConsultationDate,
                true,
                lastForm?.EncounterConsultationDate,
                callback
            );
        }
        catch (error) {
            this.handleError(error);
        }
        this.close();
    }

    private async duplicatePatientForm(form: RadPdfForm, forceDuplicate: boolean = false): Promise<RadPdfForm> {
        return this.handleClientResponse(await Client.postAsync<RadPdfForm>(
            ServerOperations.Server.Myle_Patient_MedicalNote_DuplicateForm,
            {
                RadDocumentId: form.RadDocumentId,
                FormName: form.FormName,
                EncounterId: this.props.encounter.Id,
                EncounterConsultationDate: this.props.encounter.ConsultationDate,
                ForceDuplicate: forceDuplicate,
                FormId: form.FormId
            }
        ));
    }

    private async handleOnContinueClick(form: RadPdfForm): Promise<void> {
        const response = await this.addRadPdfToEncounter(form);

        if (!response)
            return null;

        this.openRadPdfFormWithCallback(
            response.Form,
            response.LastForm,
            () => this.props.onContinue && this.props.onContinue(response.Form, response.IsEncounterAdded)
        );
    }

    private async addRadPdfToEncounter(form: RadPdfForm): Promise<any> {
        return this.handleClientResponse(await Client.postAsync<any>(
            ServerOperations.Server.Myle_Patient_MedicalNote_AddRadPdfToEncounter,
            {
                EncounterId: this.props.encounter.Id,
                PatientFormId: form.PatientFormId
            }
        ));
    }

    private handleError(errors: ErrorMessage[]): void {
        if (!errors?.length)
            return;

        sessionStore.showPopup(errors);
        this.close();
    }

    private loadNewForms(): void {
        cacheStore.getPDFForms(
            forms => this.mutateState(s => {
                let favoriteFormsByIds = cacheStore.FavoriteForms.toLookup(p => p.FormId);

                const allForms = (forms || []).map(form => GenericForm.fromRadPDFForm(form, favoriteFormsByIds));
                s.allForms = allForms.map(x => x.toSearchableItemModelT());
                s.newFormDisplayableList = allForms.sortByPropertyDesc(x => x.isFavorite);
            })
        );
    }

    private async loadRecentlyUsedForms(): Promise<any> {
        if (!this.props.encounter)
            return null;

        return this.handleClientResponse(await Client.postAsync<any>(
            ServerOperations.Server.Myle_Patient_MedicalNote_SearchRecentlyUsedRadPdf,
            {
                EncounterId: this.props.encounter.Id
            }
        ));
    }

    private async addFavorite(form: GenericForm): Promise<any> {
        return this.handleClientResponse(await Client.postAsync<any>(
            ServerOperations.Server.Myle_Forms_AddFavoriteForm,
            {
                FormType: form.formType,
                FormId: form.formId
            }
        ));
    }

    private async removeFavorite(form: GenericForm): Promise<any> {
        return this.handleClientResponse(await Client.postAsync<any>(
            ServerOperations.Server.Myle_Forms_RemoveFavoriteForm,
            {
                FormType: form.formType,
                FormId: form.formId
            }
        ));
    }

    private close(): void {
        this.props.onClose && this.props.onClose();
    }

    private handleClientResponse<T>(clientResponse: ClientResponse<T>): T {
        if (clientResponse.isSuccess)
            return clientResponse.data;

        this.handleError(clientResponse.errors);
        return null;
    }
}

export { RadPdfFormsSelectionComponent };
